import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";

import { BulbIcon } from "@/app/assets/icons/interface/interface";

interface LangContent {
	en: React.ReactNode;
	sw: React.ReactNode;
}

interface RoleExplainerAccordionProps {
	title: string;
	content: LangContent;
}

function RoleExplainerAccordion({ title, content }: RoleExplainerAccordionProps) {
	const [open, setOpen] = useState<boolean>(true);
	const [lang, setLang] = useState<"en" | "sw">("en");

	const toggleOpen = () => setOpen((v) => !v);

	const handleLangToggle = (e: React.MouseEvent, next?: "en" | "sw") => {
		e.stopPropagation(); // prevent accordion toggle
		const newLang = next ?? (lang === "en" ? "sw" : "en");
		setLang(newLang);
	};

	return (
		<div
			className="w-full border rounded-2xl border-[#C9DBF0] shadow-sm"
			style={{ background: "#E9F1FB", borderColor: "#F0F7FF" }}
		>
			{/* Top header bar */}
			<div
				className="flex items-center justify-between px-4 py-3 rounded-t-2xl"
				style={{ background: "#DBEAFE", color: "#0069CB" }}
			>
				<div className="flex items-center gap-3">
					<span className="text-xl">
						<BulbIcon />
					</span>
					<span className="font-semibold text-lg"> {title}</span>
				</div>

				<div className="flex items-center gap-6">
					{/* SMALL Pill switch - reduced size */}
					<div className="select-none">
						<button
							type="button"
							role="switch"
							aria-checked={lang === "sw"}
							onClick={(e) => handleLangToggle(e)}
							className="w-16 h-8 bg-[#012B99] rounded-full border border-[#E6EEF9] flex items-center p-1 relative focus:outline-none shadow-sm"
							aria-label="Toggle language"
						>
							{/* track */}
							<div className="absolute inset-0 rounded-full" style={{ backgroundColor: "#0a6fe2" }} aria-hidden />

							{/* labels inside track */}
							<div className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
								<span className={`text-xs font-medium ${lang === "sw" ? "text-white" : "text-[#9CA3AF]"}`}>SW</span>
								<span className={`text-xs font-medium ${lang === "en" ? "text-white" : "text-[#9CA3AF]"}`}>EN</span>
							</div>

							{/* small knob */}
							<div
								className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transform transition-all duration-200 ${
									lang === "sw" ? "right-1" : "left-1"
								}`}
								style={{ pointerEvents: "none" }}
							/>
						</button>
					</div>

					{/* Chevron button controls accordion open/close */}
					<button
						type="button"
						onClick={toggleOpen}
						aria-expanded={open}
						className="p-1 rounded-full hover:bg-[#CFE1FA] transition-transform"
						style={{ color: "#012B99" }}
					>
						<ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
					</button>
				</div>
			</div>

			{/* Accordion (controlled) */}
			<Accordion.Root
				type="single"
				collapsible
				value={open ? "item-1" : undefined}
				onValueChange={(v) => setOpen(Boolean(v))}
			>
				<Accordion.Item value="item-1" className="rounded-b-2xl overflow-hidden">
					<Accordion.Content
						className="text-[#2B2B2B] text-base leading-relaxed px-3 py-3 space-y-4 data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown"
						style={{ background: "#F0F7FF", borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}
					>
						<div className="bg-white p-4 rounded-md ">{lang === "en" ? content.en : content.sw}</div>
					</Accordion.Content>
				</Accordion.Item>
			</Accordion.Root>
		</div>
	);
}

export default RoleExplainerAccordion;
